import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Subscription, combineLatest, interval, takeUntil } from 'rxjs';

import { LoaderComponent } from './loader/loader.component';
import { CountdownService } from '../../services/countdown.service';

@Component({
    selector: 'app-count-down',
    imports: [CommonModule, LoaderComponent],
    templateUrl: './countdown.component.html',
    styleUrl: './countdown.component.scss'
})
export class CountDownComponent implements OnInit {
    private static readonly secondsToMsFactor = 1000;
    private static readonly updateIntervalMs = 200;
    private static readonly startingHue = 160;
    private static readonly fullCircle = 360;

    @Input()
    initialTimeInSeconds!: number;

    @Input()
    animate!: boolean;

    @Output()
    readonly countDownProgressed = new EventEmitter<number>();

    @Output()
    readonly countDownFinished = new EventEmitter<true>();

    protected remainingTimeInSeconds!: number;
    protected remainingAmount = CountDownComponent.fullCircle;
    protected color = '';
    protected isVisible = true;

    private endTime = 0;
    private counterSubscription?: Subscription;

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly countdown: CountdownService
    ) {}

    ngOnInit(): void {
        this.countdown.resume();
        this.countdown.restartCountdown$.subscribe(() => this.restartCountDown());
        this.startCounter();
    }

    startCounter(): void {
        this.remainingTimeInSeconds = this.initialTimeInSeconds;
        this.endTime = Date.now() + CountDownComponent.updateIntervalMs + this.secondsToMs(this.initialTimeInSeconds);

        this.counterSubscription = combineLatest([interval(CountDownComponent.updateIntervalMs), this.countdown.pauseCountdown$])
            .pipe(takeUntil(this.countDownFinished))
            .subscribe(([_intervalValue, isPaused]) => {
                if (isPaused) {
                    return;
                }

                this.updateProgressValues(this.endTime);

                if (Date.now() >= this.endTime) {
                    this.remainingAmount = CountDownComponent.fullCircle;
                    this.countDownFinished.emit(true);
                }
            });
    }

    private restartCountDown(): void {
        this.counterSubscription?.unsubscribe();
        this.startCounter();
    }

    private prepareForNewCountdown(): void {
        this.remainingTimeInSeconds = this.initialTimeInSeconds;
        this.endTime = Date.now() + CountDownComponent.updateIntervalMs + this.secondsToMs(this.initialTimeInSeconds);
    }

    private secondsToMs(seconds: number): number {
        return seconds * CountDownComponent.secondsToMsFactor;
    }

    private updateProgressValues(endTime: number): void {
        // When it's 0 / 0, the result will be NaN, so we need to handle that (NaN || 0).
        const remainingRatio = Math.max((endTime - Date.now()) / this.secondsToMs(this.initialTimeInSeconds), 0) || 0;

        this.remainingAmount = CountDownComponent.fullCircle * remainingRatio;
        this.remainingTimeInSeconds = Math.ceil(remainingRatio * this.initialTimeInSeconds);

        const hue = CountDownComponent.startingHue * remainingRatio;
        // eslint-disable-next-line spellcheck/spell-checker
        this.color = `hsl(${hue}, 100%, 50%)`;

        this.countDownProgressed.emit(remainingRatio);
    }
}
